import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { 
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Upload as UploadIcon,
  Class as ClassIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface School {
  id: number;
  name: string;
}

interface Grade {
  id: number;
  name: string;
  school_id: number;
  teacher: number | null;
}

interface Teacher {
  id: number;
  name: string;
  email: string;
}

interface ImportFormData {
  selectedGradeId: number;
  createNewGrade: boolean;
  newGradeName: string;
  newGradeSchoolId: number;
  selectedTeacherId: number | null;
}

export interface ImportStudentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: () => void;
  selectedFile: File | null;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  formData: ImportFormData;
  onFormChange: (field: string, value: string | number | boolean | null) => void;
  schools: School[];
  grades: Grade[];
  teachers: Teacher[];
  isImporting: boolean;
}

const ImportStudentsModal: React.FC<ImportStudentsModalProps> = ({
  isOpen,
  onClose,
  onImport,
  selectedFile,
  onFileChange,
  formData,
  onFormChange,
  schools,
  grades,
  teachers,
  isImporting,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Dialog
      open={isOpen}
      onClose={isImporting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown={isImporting}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: isMobile ? 0 : 3,
          position: 'relative',
        }
      }}
    >
      <IconButton
        onClick={onClose}
        disabled={isImporting}
        className="close-button"
        sx={{ color: '#666' }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle 
        sx={{ 
          textAlign: 'center',
          pb: 2,
          pt: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}
      >
        <div 
          className="modal-icon"
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(33, 150, 243, 0.3)',
          }}
        >
          <CloudUploadIcon sx={{ color: 'white', fontSize: 32 }} />
        </div>
        {t("importStudentsFromExcel")}
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2, px: 3 }}>
        <div className="import-students-content">
          <div className="instructions-section">
            <div className="instruction-header">
              <span role="img" aria-label="קלסר">📋</span> {t("excelFileWithColumns")}
            </div>
            <div className="instruction-text">{t("selectExistingOrCreateNewClass")}</div>
          </div>

          <div className="file-upload-section">
            <div className="section-label">
              <span role="img" aria-label="דף">📄</span> {t("selectExcelFile")}
            </div>
            <div 
              className={`file-drop-zone ${selectedFile ? 'has-file' : ''}`}
              onClick={() => document.getElementById("file-input")?.click()}
            >
              <input
                id="file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={onFileChange}
                style={{ display: "none" }}
              />
              {selectedFile ? (
                <div className="file-selected">
                  <UploadIcon className="upload-icon" />
                  <div className="file-name">
                    <span role="img" aria-label="סימון וי">✅</span> {selectedFile.name}
                  </div>
                  <div className="file-change-hint">{t("clickToChange")}</div>
                </div>
              ) : (
                <div className="file-empty">
                  <CloudUploadIcon className="upload-icon-empty" />
                  <div className="file-prompt">{t("clickToSelectExcelFile")}</div>
                  <div className="drag-hint">{t("orDragAndDropHere")}</div>
                </div>
              )}
            </div>
          </div>

          <div className="class-selection-section">
            <div className="section-label">
              <span role="img" aria-label="מטרה">🎯</span> {t("importTarget")}
            </div>
            
            <div className="class-options">
              <div 
                className={`class-option ${!formData.createNewGrade ? 'selected' : ''}`}
                onClick={() => onFormChange("createNewGrade", false)}
              >
                <div className="option-header">
                  <div className={`radio-button ${!formData.createNewGrade ? 'selected' : ''}`}>
                    {!formData.createNewGrade && <div className="radio-dot" />}
                  </div>
                  <div className="option-title">
                    <span role="img" aria-label="כובע גמר">🎓</span> {t("existingClass")}
                  </div>
                </div>
                
                {!formData.createNewGrade && (
                  <div className="option-content">
                    {grades.length === 0 ? (
                      <div className="no-classes-warning">
                        <ClassIcon className="warning-icon" />
                        <div className="warning-title">{t("noClassesInSystem")}</div>
                        <div className="warning-text">{t("mustCreateNewClass")}</div>
                      </div>
                    ) : (
                      <div className="grades-list">
                        {grades.map((grade) => {
                          const isSelected = formData.selectedGradeId === grade.id;
                          const gradeTeacher = grade.teacher ? teachers.find(t => t.id === grade.teacher) : null;
                          
                          return (
                            <div
                              key={grade.id}
                              className={`grade-item ${isSelected ? 'selected' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onFormChange("selectedGradeId", grade.id);
                              }}
                            >
                              <div className="grade-name">{grade.name}</div>
                              <div className="grade-details">
                                <div className="school-name">{schools.find(s => s.id === grade.school_id)?.name}</div>
                                {gradeTeacher && <div className="teacher-name">{gradeTeacher.name}</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div 
                className={`class-option ${formData.createNewGrade ? 'selected' : ''}`}
                onClick={() => onFormChange("createNewGrade", true)}
              >
                <div className="option-header">
                  <div className={`radio-button ${formData.createNewGrade ? 'selected' : ''}`}>
                    {formData.createNewGrade && <div className="radio-dot" />}
                  </div>
                  <div className="option-title">
                    <span role="img" aria-label="כוכב">✨</span> {t("createNewClass")}
                  </div>
                </div>

                {formData.createNewGrade && (
                  <div className="option-content">
                    <TextField
                      fullWidth
                      margin="normal"
                      label={t("className")}
                      value={formData.newGradeName}
                      onChange={(e) => onFormChange("newGradeName", e.target.value)}
                      disabled={isImporting}
                      required
                      size="small"
                    />
                    
                    <FormControl fullWidth margin="normal" size="small" disabled={isImporting}>
                      <InputLabel>{t("school")}</InputLabel>
                      <Select
                        value={formData.newGradeSchoolId}
                        label={t("school")}
                        onChange={(e) => onFormChange("newGradeSchoolId", e.target.value)}
                      >
                        {schools.map((school) => (
                          <MenuItem key={school.id} value={school.id}>
                            {school.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal" size="small" disabled={isImporting}>
                      <InputLabel>{t("assignedTeacher")} ({t("optional")})</InputLabel>
                      <Select
                        value={formData.selectedTeacherId || ""}
                        label={`${t("assignedTeacher")} (${t("optional")})`}
                        onChange={(e) => onFormChange("selectedTeacherId", e.target.value || null)}
                      >
                        <MenuItem value="">{t("noTeacher")}</MenuItem>
                        {teachers
                          .filter(teacher => !grades.some(g => g.teacher === teacher.id))
                          .map((teacher) => (
                            <MenuItem key={teacher.id} value={teacher.id}>
                              {teacher.name} ({teacher.email})
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }} style={{ marginBottom: 0 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isImporting}
          sx={{ minWidth: 100 }}
        >
          {t("cancel")}
        </Button>
        <Button
          onClick={onImport}
          variant="contained"
          disabled={
            isImporting || 
            !selectedFile ||
            (!formData.createNewGrade && !formData.selectedGradeId) ||
            (formData.createNewGrade && (!formData.newGradeName || !formData.newGradeSchoolId))
          }
          sx={{ minWidth: 120 }}
        >
          {isImporting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {t("importing")}
            </>
          ) : (
            t("startImport")
          )}
        </Button>
      </DialogActions>

      {/* Loading Overlay */}
      {isImporting && (
        <div 
          className="loading-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            borderRadius: isMobile ? 0 : 12,
          }}
        >
          <CircularProgress 
            size={60}
            sx={{ color: '#2196f3', mb: 2 }}
          />
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#2c3e50', textAlign: 'center', marginBottom: '8px' }}>
            {t("importingStudents")}
          </div>
          <div style={{ fontSize: '14px', color: '#7f8c8d', textAlign: 'center' }}>
            {t("pleaseWait")}
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default ImportStudentsModal;